import { navigate, withPrefix } from 'gatsby'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { Loading } from '../../components/loading'
import Login from '../../pages/login'
import { getWindowObject } from '../../utils/browser-api'
import { getDebugLogger } from '../../utils/logger'
import '../api-adapters/auth'
import { subscribeToUserdata, unsubscribeFromUserdata } from '../api-adapters/auth'

// Types
import type { IUserdata } from '../api-adapters/auth'

const LOGIN_ROUTE = '/login'
const DEBUG_PREFIX = 'Auth.Context'
const ACCESS_TOKEN_INIT_DELAY = 1000
const REDIRECT_RELOAD_DELAY = 500

const debug = getDebugLogger(DEBUG_PREFIX)

interface IAuthContextValue {
  isLoading: boolean
  isFetching: boolean
  isTokenExpired?: boolean
  error?: any
  authData: {
    userId?: string
    accessToken?: string
    userdata?: IUserdata
  }
}

const AuthContext = createContext<IAuthContextValue>({
  isLoading: true,
  isFetching: true,
  isTokenExpired: undefined,
  authData: {
    userId: undefined,
    accessToken: undefined,
    userdata: undefined,
  }
})

debug('module')

function AuthProvider({ children }) {
  const [userdata, setUserdata] = useState<IUserdata | undefined>(undefined)

  const userId = userdata?.id
  const accessToken = userdata?.accessToken
  const isTokenExpired = userdata?.isTokenExpired
  debug('AuthProvider', { userdata, userId, accessToken })

  useEffect(() => {
    debug('AuthProvider.useEffect add')
    function handleUserdataChange(userdata: IUserdata) {
      debug('handleUserdataChange', { userdata })
      setUserdata(userdata)
    }
    subscribeToUserdata(handleUserdataChange)
    return () => {
      debug('AuthProvider.useEffect remove')
      unsubscribeFromUserdata(handleUserdataChange)
    }
  }, [])

  const value: IAuthContextValue = {
    isLoading: userId == null || accessToken == null || isTokenExpired == true,
    isFetching: false,
    isTokenExpired,
    authData: {
      userId,
      accessToken,
      userdata,
    },
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

function useAuth() {
  const context = useContext(AuthContext)
  debug('useAuth', { context })
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

function AuthGuard({children}) {
  const auth = useAuth()

  const window = getWindowObject()
  const url = new URL(window.location.href)
  const loginUrl = withPrefix('/login')
  debug('AuthGuard', { pathname: url.pathname, loginUrl, auth })

  if (url.pathname === loginUrl || url.pathname === loginUrl + '/') {
    return <Login /> // TODO(Auth): decouple context from components (potentially a circular dependency)
  }

  useEffect(() => {
    // Wait for access token to initialize and then redirect to /login if required
    if (url.pathname !== loginUrl && url.pathname !== loginUrl + '/') {
      const timeoutId = setTimeout(() => {
        if (auth.authData.accessToken == undefined || auth.isTokenExpired) {
          navigate('/login')
          setTimeout(() => {
            window.location.reload()
          }, REDIRECT_RELOAD_DELAY)
        }
      }, ACCESS_TOKEN_INIT_DELAY)
      return () => {
        clearTimeout(timeoutId)
      }
    } else {
      return () => {}
    }
  }, [auth, navigate])

  if (auth.isLoading) {
    return <Loading /> // TODO(Auth): decouple context from components (potentially a circular dependency)
  }

  return <>{children}</>
}

export {
  AuthGuard, AuthProvider, LOGIN_ROUTE, useAuth
}

